import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";
import { LayoutGrid, Users, CreditCard, User, LogOut, Database, PanelLeftClose, PanelLeftOpen, Puzzle, ShieldCheck, X } from "lucide-react";
import { useSiteName } from "../../hooks/useSiteName";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutGrid, testId: "nav-dashboard" },
  { to: "/leads", label: "Leads", icon: Users, testId: "nav-leads" },
  { to: "/subscriptions", label: "Subscriptions", icon: CreditCard, testId: "nav-subscriptions" },
  // { to: "/integrations", label: "Integrations", icon: Puzzle, testId: "nav-integrations" },
  { to: "/profile", label: "Profile", icon: User, testId: "nav-profile" },
];

export default function Sidebar() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { sidebarCollapsed, toggleSidebar, mobileSidebarOpen, closeMobileSidebar } = useUIStore();
  const collapsed = sidebarCollapsed;
  const siteName = useSiteName();

  // On mobile (< md), the sidebar is an off-canvas drawer driven by `mobileSidebarOpen`.
  // On md+, it behaves as the existing sticky/collapsible sidebar (mobile state ignored).
  const onNavClick = () => closeMobileSidebar();

  return (
    <>
      {/* Mobile backdrop */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={closeMobileSidebar}
          data-testid="sidebar-backdrop"
        />
      )}

      <aside
        className={`
          ${collapsed ? "md:w-16" : "md:w-60"}
          fixed md:sticky inset-y-0 left-0 z-40 top-0 h-screen
          w-64 md:transition-[width] duration-200 flex-shrink-0
          border-r border-zinc-200 bg-zinc-50 flex flex-col
          transform md:transform-none transition-transform duration-200
          ${mobileSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        `}
        data-testid="sidebar"
      >
        <div className={`${collapsed ? "md:px-2" : "md:px-4"} px-4 py-4 border-b border-zinc-200 flex items-center ${collapsed ? "md:justify-center" : "justify-between"} gap-2 relative`}>
          <div className={`flex items-center gap-2 ${collapsed ? "" : "min-w-0"}`}>
            <Database className="h-5 w-5 text-zinc-950 flex-shrink-0" strokeWidth={2.5} />
            {(!collapsed || mobileSidebarOpen) && (
              <div className={`min-w-0 ${collapsed ? "md:hidden" : ""}`}>
                <span
                  className="text-sm font-black tracking-tight text-zinc-950 block leading-tight truncate"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  data-testid="sidebar-site-name"
                >
                  {siteName}
                </span>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">Agent Console</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* Mobile close */}
            <button
              data-testid="sidebar-mobile-close"
              onClick={closeMobileSidebar}
              title="Close menu"
              className="md:hidden p-1.5 rounded-md border border-zinc-200 bg-white text-zinc-600 hover:text-zinc-950 hover:border-zinc-950 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            {/* Desktop collapse */}
            {!collapsed && (
              <button
                data-testid="sidebar-toggle"
                onClick={toggleSidebar}
                title="Collapse sidebar"
                className="hidden md:flex p-1.5 rounded-md border border-zinc-200 bg-white text-zinc-600 hover:text-zinc-950 hover:border-zinc-950 transition-colors flex-shrink-0"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        {collapsed && (
          <button
            data-testid="sidebar-toggle"
            onClick={toggleSidebar}
            title="Expand sidebar"
            className="hidden md:flex mx-2 mt-2 p-2 rounded-md border border-zinc-200 bg-white text-zinc-600 hover:text-zinc-950 hover:border-zinc-950 items-center justify-center transition-colors"
          >
            <PanelLeftOpen className="h-4 w-4" />
          </button>
        )}

        <nav className={`flex-1 ${collapsed ? "md:px-2" : "md:px-3"} px-3 py-4 flex flex-col gap-0.5 overflow-y-auto`}>
          {navItems.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testId}
              onClick={onNavClick}
              title={collapsed ? label : undefined}
              className={({ isActive }) =>
                `flex items-center ${collapsed ? "md:justify-center" : ""} gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-zinc-950 text-white" : "text-zinc-700 hover:bg-zinc-100"
                }`
              }
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span className={collapsed ? "md:hidden" : ""}>{label}</span>
            </NavLink>
          ))}
          {user?.is_admin && (
            <>
              <p className={`px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-zinc-400 ${collapsed ? "md:hidden" : ""}`}>Admin Actions</p>
              {collapsed && <div className="hidden md:block my-2 border-t border-zinc-200" />}
              <NavLink
                to="/admin/subscriptions"
                data-testid="nav-admin-subscriptions"
                onClick={onNavClick}
                title={collapsed ? "Manage Subscriptions" : undefined}
                className={({ isActive }) =>
                  `flex items-center ${collapsed ? "md:justify-center" : ""} gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-zinc-950 text-white" : "text-zinc-700 hover:bg-zinc-100"
                  }`
                }
              >
                <ShieldCheck className="h-4 w-4 flex-shrink-0" />
                <span className={collapsed ? "md:hidden" : ""}>Manage Subscriptions</span>
              </NavLink>
            </>
          )}
        </nav>

        <div className={`${collapsed ? "md:px-2" : "md:px-3"} px-3 py-4 border-t border-zinc-200`}>
          <div className={`px-3 py-2 mb-2 ${collapsed ? "md:hidden" : ""}`}>
            <p className="text-sm font-semibold text-zinc-950 truncate" data-testid="sidebar-user-name">
              {user?.full_name}
            </p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>
          <button
            data-testid="logout-button"
            onClick={() => {
              logout();
              closeMobileSidebar();
              navigate("/login");
            }}
            title={collapsed ? "Log out" : undefined}
            className={`w-full flex items-center ${collapsed ? "md:justify-center" : ""} gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100`}
          >
            <LogOut className="h-4 w-4 flex-shrink-0" />
            <span className={collapsed ? "md:hidden" : ""}>Log out</span>
          </button>
        </div>
      </aside>
    </>
  );
}
